import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface Case {
  id: string;
  clientId: string;
  clientName: string | null;
  leadId: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  feeRuleId: string | null;
  feeRuleName: string | null;
  title: string;
  caseNumber: string | null;
  caseNumberNormalized: string | null;
  caseNumberKind: string;
  caseNumberValid: boolean | null;
  filingYear: number | null;
  judiciarySegment: number | null;
  tribunalCode: string | null;
  originUnitCode: string | null;
  court: string | null;
  courtDivision: string | null;
  legalArea: string | null;
  opposingParty: string | null;
  status: string;
  phase: string | null;
  priority: string;
  responsibleUser: string | null;
  caseValue: number | null;
  contractedFee: number | null;
  successFeePercentage: number | null;
  startDate: string | null;
  endDate: string | null;
  nextDeadlineAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCaseInput {
  clientId: string;
  leadId?: string | null;
  legalProductId?: string | null;
  feeRuleId?: string | null;
  title: string;
  caseNumber?: string | null;
  court?: string | null;
  courtDivision?: string | null;
  legalArea?: string | null;
  opposingParty?: string | null;
  status?: string;
  phase?: string | null;
  priority?: string;
  responsibleUser?: string | null;
  caseValue?: number | null;
  contractedFee?: number | null;
  successFeePercentage?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  nextDeadlineAt?: string | null;
  notes?: string | null;
}

export type UpdateCaseInput = CreateCaseInput;

export interface CaseLoadFilters {
  status?: string;
  clientId?: string;
  legalProductId?: string;
}

type UseCasesOptions = {
  autoLoad?: boolean;
};

const allowedStatuses = new Set([
  'active',
  'waiting_client',
  'waiting_court',
  'hearing_scheduled',
  'completed',
  'archived',
]);

const allowedPriorities = new Set(['low', 'normal', 'high', 'urgent']);

const legacyStatusMap: Record<string, string> = {
  'Análise inicial': 'waiting_client',
  'Documentos pendentes': 'waiting_client',
  'Contrato enviado': 'waiting_client',
  Ativo: 'active',
  'Peça em elaboração': 'waiting_court',
  Protocolado: 'waiting_court',
  'Aguardando decisão': 'waiting_court',
  'Audiência marcada': 'hearing_scheduled',
  Finalizado: 'completed',
  Arquivado: 'archived',
};

const legacyPriorityMap: Record<string, string> = {
  Baixa: 'low',
  Normal: 'normal',
  Alta: 'high',
  Urgente: 'urgent',
  'Prazo fatal': 'urgent',
};

const caseStatusLabelMap: Record<string, string> = {
  active: 'Ativo',
  waiting_client: 'Aguardando cliente',
  waiting_court: 'Aguardando juízo',
  hearing_scheduled: 'Audiência marcada',
  completed: 'Concluído',
  archived: 'Arquivado',
};

const casePriorityLabelMap: Record<string, string> = {
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toCaseStatus = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'active';
  }

  const normalized = text.toLowerCase();
  if (allowedStatuses.has(normalized)) {
    return normalized;
  }

  return legacyStatusMap[text] ?? 'active';
};

const toCasePriority = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'normal';
  }

  const normalized = text.toLowerCase();
  if (allowedPriorities.has(normalized)) {
    return normalized;
  }

  return legacyPriorityMap[text] ?? 'normal';
};

const sortCasesByCreatedAtDesc = (a: Case, b: Case) => {
  const left = Date.parse(a.createdAt);
  const right = Date.parse(b.createdAt);

  if (Number.isFinite(left) && Number.isFinite(right)) {
    return right - left;
  }

  return b.id.localeCompare(a.id);
};

const normalizeCase = (value: any): Case => ({
  id: String(value?.id ?? ''),
  clientId: toNullableText(value?.clientId ?? value?.client_id) ?? '',
  clientName: toNullableText(value?.clientName ?? value?.client_name),
  leadId: toNullableText(value?.leadId ?? value?.lead_id),
  legalProductId: toNullableText(value?.legalProductId ?? value?.legal_product_id),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  feeRuleId: toNullableText(value?.feeRuleId ?? value?.fee_rule_id),
  feeRuleName: toNullableText(value?.feeRuleName ?? value?.fee_rule_name),
  title: String(value?.title ?? '').trim(),
  caseNumber: toNullableText(value?.caseNumber ?? value?.case_number),
  caseNumberNormalized: toNullableText(
    value?.caseNumberNormalized ?? value?.case_number_normalized
  ),
  caseNumberKind:
    toNullableText(
      value?.caseNumberKind ?? value?.case_number_kind
    ) ?? 'external',
  caseNumberValid:
    value?.caseNumberValid === true ||
    value?.case_number_valid === 1
      ? true
      : value?.caseNumberValid === false ||
          value?.case_number_valid === 0
        ? false
        : null,
  filingYear: toNullableNumber(
    value?.filingYear ?? value?.filing_year
  ),
  judiciarySegment: toNullableNumber(
    value?.judiciarySegment ?? value?.judiciary_segment
  ),
  tribunalCode: toNullableText(
    value?.tribunalCode ?? value?.tribunal_code
  ),
  originUnitCode: toNullableText(
    value?.originUnitCode ?? value?.origin_unit_code
  ),
  court: toNullableText(value?.court),
  courtDivision: toNullableText(value?.courtDivision ?? value?.court_division),
  legalArea: toNullableText(value?.legalArea ?? value?.legal_area),
  opposingParty: toNullableText(value?.opposingParty ?? value?.opposing_party),
  status: toCaseStatus(value?.status),
  phase: toNullableText(value?.phase),
  priority: toCasePriority(value?.priority),
  responsibleUser: toNullableText(value?.responsibleUser ?? value?.responsible_user),
  caseValue: toNullableNumber(value?.caseValue ?? value?.case_value),
  contractedFee: toNullableNumber(value?.contractedFee ?? value?.contracted_fee),
  successFeePercentage: toNullableNumber(value?.successFeePercentage ?? value?.success_fee_percentage),
  startDate: toNullableText(value?.startDate ?? value?.start_date),
  endDate: toNullableText(value?.endDate ?? value?.end_date),
  nextDeadlineAt: toNullableText(value?.nextDeadlineAt ?? value?.next_deadline_at),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

export function useCases(options: UseCasesOptions = {}) {
  const { autoLoad = true } = options;
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoadingCases, setIsLoadingCases] = useState(true);
  const [casesError, setCasesError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadCases = useCallback(async (filters: CaseLoadFilters = {}) => {
    const requestId = ++requestIdRef.current;
    setIsLoadingCases(true);
    setCasesError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.status?.trim()) {
        searchParams.set('status', filters.status.trim());
      }

      if (filters.clientId?.trim()) {
        searchParams.set('clientId', filters.clientId.trim());
      }

      if (filters.legalProductId?.trim()) {
        searchParams.set('legalProductId', filters.legalProductId.trim());
      }

      const endpoint = searchParams.toString() ? `/api/cases?${searchParams.toString()}` : '/api/cases';
      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.cases)) {
        throw new Error(data.error || `GET /api/cases falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        setCases(data.cases.map(normalizeCase).sort(sortCasesByCreatedAtDesc));
      }
    } catch (error) {
      console.error('Erro ao carregar casos reais. ', error);

      if (requestId === requestIdRef.current) {
        setCases([]);
        setCasesError('Não foi possível carregar os dados reais. Tente novamente.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingCases(false);
      }
    }
  }, []);

  const createCase = useCallback(async (input: CreateCaseInput) => {
    const response = await fetch('/api/cases', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.case) {
      throw new Error(data.error || `POST /api/cases falhou com status ${response.status}`);
    }

    const created = normalizeCase(data.case);
    setCases((prev) => [created, ...prev.filter((caseItem) => caseItem.id !== created.id)].sort(sortCasesByCreatedAtDesc));
    return created;
  }, []);

  const updateCase = useCallback(async (id: string, input: UpdateCaseInput) => {
    const response = await fetch(`/api/cases/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.case) {
      throw new Error(data.error || `PUT /api/cases/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeCase(data.case);
    setCases((prev) => [updated, ...prev.filter((caseItem) => caseItem.id !== updated.id)].sort(sortCasesByCreatedAtDesc));
    return updated;
  }, []);

  const deleteCase = useCallback(async (id: string) => {
    const response = await fetch(`/api/cases/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/cases/${id} falhou com status ${response.status}`);
    }

    setCases((prev) =>
      prev
        .map((caseItem) =>
          caseItem.id === id
            ? {
                ...caseItem,
                status: 'archived',
                updatedAt: new Date().toISOString(),
              }
            : caseItem
        )
        .sort(sortCasesByCreatedAtDesc)
    );

    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadCases();
  }, [autoLoad, loadCases]);

  return {
    cases,
    isLoadingCases,
    casesError,
    loadCases,
    createCase,
    updateCase,
    deleteCase,
  };
}
